import React, { useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import averageAlerts from '../../../../../assets/icons/AvrageAlerts.svg';
import driverAlerts from '../../../../../assets/icons/DriverAlerts.svg';
import criticalAlert from '../../../../../assets/icons/criticalAlert.svg';
import alarm from '../../../../../assets/icons/Alarm.svg';
import infoIcon from '../../../../../assets/icons/i.svg';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import useAxios from 'packages/novous_web/src/api/useAxios';
import {
  convertMinutesToFormattedTime,
  formatDistancePerDay,
  formatTimePerDay,
} from 'packages/novous_web/src/utils/helper';
import { title } from 'process';
import { IconButton, Tooltip } from '@mui/material';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import PermissionGuard from '../../../../../common/components/PermissionGuard';
import { PermissionSubcategory } from '../../../../../utils/permissionConstants';
type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};
const AlertDataTiles: React.FC<{
  data?: ColumnObjType;
  startTileDate: string;
  endTileDate: string;
  filters?: any;
  filterData?: { data: any; isfiltered: boolean };
}> = ({ data, startTileDate, endTileDate, filters, filterData }) => {
  let alertTilesData = [
    {
      icon: alarm,
      heading: '# Total Alerts',
      value: '0',
      description:
        'Total number of safety and behavior-related alerts generated by all active devices within the selected time period.',
      permission: {
        category: PermissionSubcategory.VEHICLE_ALERTS,
        subcategory: PermissionSubcategory.TOTAL_ALERTS,
      },
    },
    {
      icon: criticalAlert,
      heading: '# Critical Alerts',
      value: '0',
      description: 'The number of high-severity alerts like drowsiness, distraction, or harsh driving.',
      permission: {
        category: PermissionSubcategory.VEHICLE_ALERTS,
        subcategory: PermissionSubcategory.CRITICAL_VEHICLE_ALERTS,
      },
    },
    {
      icon: driverAlerts,
      heading: 'Average alerts per driver',
      value: '0',
      description: 'The average number of alerts triggered per installed ADAS device.',
      permission: {
        category: PermissionSubcategory.VEHICLE_ALERTS,
        subcategory: PermissionSubcategory.AVERAGE_ALERT_PER_DEVICE,
      },
    },
    {
      icon: averageAlerts,
      heading: 'Average Alerts Per day',
      value: '0',
      description: 'The daily average of all alerts generated across the fleet.',
      permission: {
        category: PermissionSubcategory.VEHICLE_ALERTS,
        subcategory: PermissionSubcategory.AVERAGE_ALERT_PER_DAY,
      },
    },
  ];
  const { user } = useAuth();
  const {
    data: alertData,
    error: alertError,
    loading: alertLoading,
    fetchData: fetchAlertData,
  } = useAxios<any>(`${API_ENDPOINTS.alarm.VEHICLE_ALERT_ANALYTICS}`, 'POST');
  // const { data: alert1Data, error: alert1Error, loading: alert1Loading, fetchData: fetch1AlertData } = useAxios<any>(
  //     `${API_ENDPOINTS.alarm.VEHICLE_ALERT_ALL_WARNING}`,
  //     "POST"
  // );

  // const { data: alert2Data, error: alert2Error, loading: alert2Loading, fetchData: fetch2AlertData } = useAxios<any>(
  //     `${API_ENDPOINTS.alarm.VEHICLE_ALERT_BY_WARNING}`,
  //     "POST"
  // );

  useEffect(() => {
    if (!user?.customer_id || !startTileDate || !endTileDate) return;

    const fetchData = async () => {
      const payload: any = {
        fleetId: user.customer_id,
        startDate: startTileDate,
        endDate: endTileDate,
        ...(filters?.group && { vehicleGroup: filters.group }),
        ...(filters?.vehicle && { vehicleId: filters.vehicle }),
      }
      if(filterData?.isfiltered && filterData?.data){
        if(filterData.data.vehicleGroup){
          payload.vehicleGroup=filterData.data.vehicleGroup
        }
      }
      try {
        await fetchAlertData(payload);
      } catch (error) {
        console.error('Error fetching alert data:', error);
      }
    };

    fetchData();
  }, [user?.customer_id, startTileDate, endTileDate, JSON.stringify(filters)]);

  alertTilesData[3].value = alertData?.data.avgAlertPerDay || 0;
  alertTilesData[2].value = alertData?.data.avgAlertPerDevice || 0;
  alertTilesData[1].value = alertData?.data.criticalAlert || 0;
  alertTilesData[0].value = alertData?.data.totalAlert || 0;
  const filteredTileData = useMemo(() => {
    return alertTilesData.filter((item, index) => {
      const stateName = 'Stat' + (index + 1);
      if (data) {
        if (data[stateName].name === item.heading) {
          return data[stateName].status;
        }
        return false;
      } else {
        return true;
      }
    });
  }, [data, alertTilesData]);
  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {filteredTileData.map((tile, index) => (
        <PermissionGuard
          key={index}
          category={tile.permission.category}
          subcategory={tile.permission.subcategory}
        >
          <Paper
            sx={{ borderRadius: '12px' }}
            className="border border-[#f0eff8] p-4 flex flex-col gap-1 items-start"
          >
            <div className="flex items-center gap-2 w-full">
              <IconButton
                sx={(theme) => ({
                  background: theme.palette.selected,
                  padding: '4px',
                  borderRadius: '8px',
                })}
              >
                <img src={tile?.icon} alt="icon" color="primary" />
              </IconButton>
              <div className="flex flex-1 min-w-0 flex-col justify-start gap-1">
                <Typography
                  variant="body2"
                  noWrap // Ensures text doesn't wrap and remains in a single line
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%', // Ensures it adapts to available space
                  }}
                >
                  {tile.heading}
                </Typography>
              </div>
              <Tooltip title={tile.description}>
                <img src={infoIcon} alt="info icon" title="More info" className="w-4 h-4 cursor-pointer" />
              </Tooltip>
            </div>
            <div className="flex items-center w-full gap-2 flex-nowrap">
              <div className="invisible w-6 h-6">{tile.icon}</div>
              <Typography
                variant="h6"
                fontWeight={600}
                className="flex-grow px-3 w-full whitespace-nowrap overflow-hidden text-ellipsis"
                sx={{
                  marginLeft: '15px !important',
                }}
              >
                {tile.value}
              </Typography>
            </div>
          </Paper>
        </PermissionGuard>
      ))}
    </div>
  );
};

export default AlertDataTiles;
